// Generic confirmation modal: title + optional body + Cancel + Confirm
// buttons. Controller-friendly via MenuNav, Escape = cancel, Enter inside
// the popup = confirm. Mounts into #match-modals (shared with the existing
// modal system) so it stacks above any in-game modal too.
//
// Usage:
//   const popup = showConfirmationPopup({
//     title: 'Leave online play?',
//     confirmLabel: 'Leave',
//     destructive: true,
//     onConfirm: () => exitOnline(),
//     onCancel: () => {} // optional
//   });
//   // Caller can dismiss programmatically: popup.dismiss()

import { MenuNav, type MenuItem } from '../MenuNav';
import { menuAudio } from '../MenuAudio';
import { t } from '../../i18n';

const STYLES_ID = 'confirmation-popup-styles';
const STYLES = `
.confirm-popup {
  position: fixed; inset: 0;
  display: flex; align-items: center; justify-content: center;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  z-index: 220;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #FFFFFF;
  opacity: 0;
  transition: opacity 200ms ease;
  pointer-events: auto;
}
.confirm-popup.shown { opacity: 1; }
.confirm-popup.closing { opacity: 0; }

.confirm-popup .card {
  pointer-events: auto;
  background: #1E232C;
  border-radius: 18px;
  box-shadow: 0 16px 48px rgba(0,0,0,0.5);
  padding: 32px 36px;
  min-width: 360px; max-width: 90vw;
  display: flex; flex-direction: column; align-items: center;
  gap: 22px;
  transform: scale(0.96);
  transition: transform 200ms cubic-bezier(0.2, 0.9, 0.2, 1);
}
.confirm-popup.shown .card { transform: scale(1); }

.confirm-popup .title {
  font-size: 32px;
  text-align: center;
  -webkit-text-stroke: 3px rgba(0,0,0,0.22);
  paint-order: stroke fill;
  margin: 0;
}
.confirm-popup .body {
  font-size: 20px;
  color: rgba(255,255,255,0.85);
  text-align: center;
  margin: -12px 0 0 0;
}

.confirm-popup .actions {
  display: flex; gap: 14px;
  margin-top: 4px;
}
.confirm-popup .btn {
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 24px;
  padding: 12px 28px;
  border-radius: 14px;
  transition: transform 120ms ease, box-shadow 120ms ease, filter 120ms ease;
}
.confirm-popup .btn.cancel {
  color: #1E232C;
  background: linear-gradient(180deg, #F1F9FB 0%, #C3D6DB 100%);
  box-shadow: 0 4px 0 #ADC5CC, 0 6px 14px rgba(0,0,0,0.25);
}
.confirm-popup .btn.confirm {
  color: #FFFFFF;
  -webkit-text-stroke: 3px #356B8C;
  paint-order: stroke fill;
  background: linear-gradient(180deg, #7DD2F5 0%, #3DA6FF 100%);
  box-shadow: 0 4px 0 #256AA0, 0 8px 18px rgba(0,0,0,0.3);
}
.confirm-popup .btn.confirm.destructive {
  -webkit-text-stroke: 3px #7A2A0E;
  background: linear-gradient(180deg, #FFAA66 0%, #FF6E2D 100%);
  box-shadow: 0 4px 0 #B14400, 0 8px 18px rgba(0,0,0,0.3);
}
.confirm-popup .btn:hover,
.confirm-popup .btn.focused { filter: brightness(1.04); outline: 4px solid rgba(255,255,255,0.6); outline-offset: 3px; }
.confirm-popup .btn:active { transform: translateY(3px); }
`;

export interface ConfirmationPopupOptions {
  title: string;
  body?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** When true, the confirm button uses an orange-red palette to telegraph
   *  a destructive action ("Leave", "Disband", etc.). Default focus stays
   *  on Cancel so the user has to deliberately move + activate Confirm. */
  destructive?: boolean;
  onConfirm: () => void;
  onCancel?: () => void;
}

export interface ConfirmationPopupHandle {
  dismiss(): void;
}

export function showConfirmationPopup(opts: ConfirmationPopupOptions): ConfirmationPopupHandle {
  ensureStyles();
  const root = ensureRoot();
  const wrap = document.createElement('div');
  wrap.className = 'confirm-popup';
  const cancelLabel = escapeHtml(opts.cancelLabel ?? t('confirm.cancel'));
  const confirmLabel = escapeHtml(opts.confirmLabel ?? t('confirm.confirm'));
  const destructiveCls = opts.destructive ? ' destructive' : '';
  wrap.innerHTML = `
    <div class="card">
      <div class="title">${escapeHtml(opts.title)}</div>
      ${opts.body ? `<div class="body">${escapeHtml(opts.body)}</div>` : ''}
      <div class="actions">
        <button type="button" class="btn cancel">${cancelLabel}</button>
        <button type="button" class="btn confirm${destructiveCls}">${confirmLabel}</button>
      </div>
    </div>
  `;
  root.appendChild(wrap);
  requestAnimationFrame(() => wrap.classList.add('shown'));

  let dismissed = false;

  const cancelBtn = wrap.querySelector('.btn.cancel') as HTMLButtonElement;
  const confirmBtn = wrap.querySelector('.btn.confirm') as HTMLButtonElement;

  const finish = (which: 'cancel' | 'confirm'): void => {
    if (dismissed) return;
    dismissed = true;
    nav?.dispose();
    removeEventListener('keydown', onKeyDown, { capture: true } as EventListenerOptions);
    wrap.classList.add('closing');
    setTimeout(() => wrap.remove(), 220);
    if (which === 'cancel') opts.onCancel?.();
    else opts.onConfirm();
  };

  const items: MenuItem[] = [
    { el: cancelBtn,  row: 0, col: 0, onActivate: () => finish('cancel') },
    { el: confirmBtn, row: 0, col: 1, onActivate: () => finish('confirm') }
  ];
  // Default focus on Cancel — destructive actions should require an
  // explicit user move-right before pressing A.
  const nav: MenuNav | null = new MenuNav({ items, initialIndex: 0 });
  nav.mount();

  const onKeyDown = (e: KeyboardEvent): void => {
    if (dismissed) return;
    if (e.code === 'Escape' || e.code === 'Backspace') {
      e.preventDefault();
      e.stopPropagation();
      menuAudio.playClick();
      finish('cancel');
    }
  };
  addEventListener('keydown', onKeyDown, { capture: true });

  return {
    dismiss(): void { finish('cancel'); }
  };
}

function ensureRoot(): HTMLElement {
  let root = document.getElementById('match-modals');
  if (!root) {
    root = document.createElement('div');
    root.id = 'match-modals';
    root.style.cssText = `position:fixed; inset:0; z-index:150; pointer-events:none;`;
    document.body.appendChild(root);
  }
  return root;
}

function ensureStyles(): void {
  if (document.getElementById(STYLES_ID)) return;
  const style = document.createElement('style');
  style.id = STYLES_ID;
  style.textContent = STYLES;
  document.head.appendChild(style);
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' })[c]!);
}
